import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Paper,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
  Fade
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Help as HelpIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// FAQ Data Structure
const getFaqData = (t: (key: string) => string) => {
  const categories = [
    {
      key: 'about',
      label: t('faq_cat_about'),
      order: 1
    },
    {
      key: 'licenses',
      label: t('faq_cat_licenses'),
      order: 2
    },
    {
      key: 'restrictions',
      label: t('faq_cat_restrictions'),
      order: 3
    },
    {
      key: 'usage',
      label: t('faq_cat_usage'),
      order: 4
    },
    {
      key: 'general_features',
      label: t('faq_cat_general_features'),
      order: 5
    },
    {
      key: 'buyer',
      label: t('faq_cat_buyer'),
      order: 6
    },
    {
      key: 'contributor',
      label: t('faq_cat_contributor'),
      order: 7
    },
    {
      key: 'licensing_images',
      label: t('faq_cat_licensing_images'),
      order: 8
    },
    {
      key: 'payments',
      label: t('faq_cat_payments'),
      order: 9
    }
  ];

  const faqs = [
    // 1. WHAT IS AND WHAT DOES LAYERSTOCK.COM OFFER (3 questions)
    { category: 'about', question: t('faq_about_q1'), answer: t('faq_about_a1') },
    { category: 'about', question: t('faq_about_q2'), answer: t('faq_about_a2') },
    { category: 'about', question: t('faq_about_q3'), answer: t('faq_about_a3') },

    // 2. LAYERSTOCK.COM LICENSES (2 questions)
    { category: 'licenses', question: t('faq_licenses_q1'), answer: t('faq_licenses_a1') },
    { category: 'licenses', question: t('faq_licenses_q2'), answer: t('faq_licenses_a2') },

    // 3. RESTRICTIONS OF RF & EL (2 questions)
    { category: 'restrictions', question: t('faq_restrictions_q1'), answer: t('faq_restrictions_a1') },
    { category: 'restrictions', question: t('faq_restrictions_q2'), answer: t('faq_restrictions_a2') },

    // 4. USE OF LICENSED CONTENT (9 questions)
    { category: 'usage', question: t('faq_usage_q1'), answer: t('faq_usage_a1') },
    { category: 'usage', question: t('faq_usage_q2'), answer: t('faq_usage_a2') },
    { category: 'usage', question: t('faq_usage_q3'), answer: t('faq_usage_a3') },
    { category: 'usage', question: t('faq_usage_q4'), answer: t('faq_usage_a4') },
    { category: 'usage', question: t('faq_usage_q5'), answer: t('faq_usage_a5') },
    { category: 'usage', question: t('faq_usage_q6'), answer: t('faq_usage_a6') },
    { category: 'usage', question: t('faq_usage_q7'), answer: t('faq_usage_a7') },
    { category: 'usage', question: t('faq_usage_q8'), answer: t('faq_usage_a8') },
    { category: 'usage', question: t('faq_usage_q9'), answer: t('faq_usage_a9') },

    // 5. GENERAL FEATURES OF WEBSITE OPERATION (16 questions)
    { category: 'general_features', question: t('faq_features_q1'), answer: t('faq_features_a1') },
    { category: 'general_features', question: t('faq_features_q2'), answer: t('faq_features_a2') },
    { category: 'general_features', question: t('faq_features_q3'), answer: t('faq_features_a3') },
    { category: 'general_features', question: t('faq_features_q4'), answer: t('faq_features_a4') },
    { category: 'general_features', question: t('faq_features_q5'), answer: t('faq_features_a5') },
    { category: 'general_features', question: t('faq_features_q6'), answer: t('faq_features_a6') },
    { category: 'general_features', question: t('faq_features_q7'), answer: t('faq_features_a7') },
    { category: 'general_features', question: t('faq_features_q8'), answer: t('faq_features_a8') },
    { category: 'general_features', question: t('faq_features_q9'), answer: t('faq_features_a9') },
    { category: 'general_features', question: t('faq_features_q10'), answer: t('faq_features_a10') },
    { category: 'general_features', question: t('faq_features_q11'), answer: t('faq_features_a11') },
    { category: 'general_features', question: t('faq_features_q12'), answer: t('faq_features_a12') },
    { category: 'general_features', question: t('faq_features_q13'), answer: t('faq_features_a13') },
    { category: 'general_features', question: t('faq_features_q14'), answer: t('faq_features_a14') },
    { category: 'general_features', question: t('faq_features_q15'), answer: t('faq_features_a15') },
    { category: 'general_features', question: t('faq_features_q16'), answer: t('faq_features_a16') },

    // 6. BUYER QUESTIONS (26 questions)
    { category: 'buyer', question: t('faq_buyer_q1'), answer: t('faq_buyer_a1') },
    { category: 'buyer', question: t('faq_buyer_q2'), answer: t('faq_buyer_a2') },
    { category: 'buyer', question: t('faq_buyer_q3'), answer: t('faq_buyer_a3') },
    { category: 'buyer', question: t('faq_buyer_q4'), answer: t('faq_buyer_a4') },
    { category: 'buyer', question: t('faq_buyer_q5'), answer: t('faq_buyer_a5') },
    { category: 'buyer', question: t('faq_buyer_q6'), answer: t('faq_buyer_a6') },
    { category: 'buyer', question: t('faq_buyer_q7'), answer: t('faq_buyer_a7') },
    { category: 'buyer', question: t('faq_buyer_q8'), answer: t('faq_buyer_a8') },
    { category: 'buyer', question: t('faq_buyer_q9'), answer: t('faq_buyer_a9') },
    { category: 'buyer', question: t('faq_buyer_q10'), answer: t('faq_buyer_a10') },
    { category: 'buyer', question: t('faq_buyer_q11'), answer: t('faq_buyer_a11') },
    { category: 'buyer', question: t('faq_buyer_q12'), answer: t('faq_buyer_a12') },
    { category: 'buyer', question: t('faq_buyer_q13'), answer: t('faq_buyer_a13') },
    { category: 'buyer', question: t('faq_buyer_q14'), answer: t('faq_buyer_a14') },
    { category: 'buyer', question: t('faq_buyer_q15'), answer: t('faq_buyer_a15') },
    { category: 'buyer', question: t('faq_buyer_q16'), answer: t('faq_buyer_a16') },
    { category: 'buyer', question: t('faq_buyer_q17'), answer: t('faq_buyer_a17') },
    { category: 'buyer', question: t('faq_buyer_q18'), answer: t('faq_buyer_a18') },
    { category: 'buyer', question: t('faq_buyer_q19'), answer: t('faq_buyer_a19') },
    { category: 'buyer', question: t('faq_buyer_q20'), answer: t('faq_buyer_a20') },
    { category: 'buyer', question: t('faq_buyer_q21'), answer: t('faq_buyer_a21') },
    { category: 'buyer', question: t('faq_buyer_q22'), answer: t('faq_buyer_a22') },
    { category: 'buyer', question: t('faq_buyer_q23'), answer: t('faq_buyer_a23') },
    { category: 'buyer', question: t('faq_buyer_q24'), answer: t('faq_buyer_a24') },
    { category: 'buyer', question: t('faq_buyer_q25'), answer: t('faq_buyer_a25') },
    { category: 'buyer', question: t('faq_buyer_q26'), answer: t('faq_buyer_a26') },

    // 7. CONTRIBUTOR INFORMATION (22 questions)
    { category: 'contributor', question: t('faq_contributor_q1'), answer: t('faq_contributor_a1') },
    { category: 'contributor', question: t('faq_contributor_q2'), answer: t('faq_contributor_a2') },
    { category: 'contributor', question: t('faq_contributor_q3'), answer: t('faq_contributor_a3') },
    { category: 'contributor', question: t('faq_contributor_q4'), answer: t('faq_contributor_a4') },
    { category: 'contributor', question: t('faq_contributor_q5'), answer: t('faq_contributor_a5') },
    { category: 'contributor', question: t('faq_contributor_q6'), answer: t('faq_contributor_a6') },
    { category: 'contributor', question: t('faq_contributor_q7'), answer: t('faq_contributor_a7') },
    { category: 'contributor', question: t('faq_contributor_q8'), answer: t('faq_contributor_a8') },
    { category: 'contributor', question: t('faq_contributor_q9'), answer: t('faq_contributor_a9') },
    { category: 'contributor', question: t('faq_contributor_q10'), answer: t('faq_contributor_a10') },
    { category: 'contributor', question: t('faq_contributor_q11'), answer: t('faq_contributor_a11') },
    { category: 'contributor', question: t('faq_contributor_q12'), answer: t('faq_contributor_a12') },
    { category: 'contributor', question: t('faq_contributor_q13'), answer: t('faq_contributor_a13') },
    { category: 'contributor', question: t('faq_contributor_q14'), answer: t('faq_contributor_a14') },
    { category: 'contributor', question: t('faq_contributor_q15'), answer: t('faq_contributor_a15') },
    { category: 'contributor', question: t('faq_contributor_q16'), answer: t('faq_contributor_a16') },
    { category: 'contributor', question: t('faq_contributor_q17'), answer: t('faq_contributor_a17') },
    { category: 'contributor', question: t('faq_contributor_q18'), answer: t('faq_contributor_a18') },
    { category: 'contributor', question: t('faq_contributor_q19'), answer: t('faq_contributor_a19') },
    { category: 'contributor', question: t('faq_contributor_q20'), answer: t('faq_contributor_a20') },
    { category: 'contributor', question: t('faq_contributor_q21'), answer: t('faq_contributor_a21') },
    { category: 'contributor', question: t('faq_contributor_q22'), answer: t('faq_contributor_a22') },

    // 8. LICENSING IMAGES (20 questions)
    { category: 'licensing_images', question: t('faq_licensing_q1'), answer: t('faq_licensing_a1') },
    { category: 'licensing_images', question: t('faq_licensing_q2'), answer: t('faq_licensing_a2') },
    { category: 'licensing_images', question: t('faq_licensing_q3'), answer: t('faq_licensing_a3') },
    { category: 'licensing_images', question: t('faq_licensing_q4'), answer: t('faq_licensing_a4') },
    { category: 'licensing_images', question: t('faq_licensing_q5'), answer: t('faq_licensing_a5') },
    { category: 'licensing_images', question: t('faq_licensing_q6'), answer: t('faq_licensing_a6') },
    { category: 'licensing_images', question: t('faq_licensing_q7'), answer: t('faq_licensing_a7') },
    { category: 'licensing_images', question: t('faq_licensing_q8'), answer: t('faq_licensing_a8') },
    { category: 'licensing_images', question: t('faq_licensing_q9'), answer: t('faq_licensing_a9') },
    { category: 'licensing_images', question: t('faq_licensing_q10'), answer: t('faq_licensing_a10') },
    { category: 'licensing_images', question: t('faq_licensing_q11'), answer: t('faq_licensing_a11') },
    { category: 'licensing_images', question: t('faq_licensing_q12'), answer: t('faq_licensing_a12') },
    { category: 'licensing_images', question: t('faq_licensing_q13'), answer: t('faq_licensing_a13') },
    { category: 'licensing_images', question: t('faq_licensing_q14'), answer: t('faq_licensing_a14') },
    { category: 'licensing_images', question: t('faq_licensing_q15'), answer: t('faq_licensing_a15') },
    { category: 'licensing_images', question: t('faq_licensing_q16'), answer: t('faq_licensing_a16') },
    { category: 'licensing_images', question: t('faq_licensing_q17'), answer: t('faq_licensing_a17') },
    { category: 'licensing_images', question: t('faq_licensing_q18'), answer: t('faq_licensing_a18') },
    { category: 'licensing_images', question: t('faq_licensing_q19'), answer: t('faq_licensing_a19') },
    { category: 'licensing_images', question: t('faq_licensing_q20'), answer: t('faq_licensing_a20') },

    // 9. CONTRIBUTOR PAYMENTS (11 questions)
    { category: 'payments', question: t('faq_payments_q1'), answer: t('faq_payments_a1') },
    { category: 'payments', question: t('faq_payments_q2'), answer: t('faq_payments_a2') },
    { category: 'payments', question: t('faq_payments_q3'), answer: t('faq_payments_a3') },
    { category: 'payments', question: t('faq_payments_q4'), answer: t('faq_payments_a4') },
    { category: 'payments', question: t('faq_payments_q5'), answer: t('faq_payments_a5') },
    { category: 'payments', question: t('faq_payments_q6'), answer: t('faq_payments_a6') },
    { category: 'payments', question: t('faq_payments_q7'), answer: t('faq_payments_a7') },
    { category: 'payments', question: t('faq_payments_q8'), answer: t('faq_payments_a8') },
    { category: 'payments', question: t('faq_payments_q9'), answer: t('faq_payments_a9') },
    { category: 'payments', question: t('faq_payments_q10'), answer: t('faq_payments_a10') },
    { category: 'payments', question: t('faq_payments_q11'), answer: t('faq_payments_a11') }
  ];

  return { categories, faqs };
};

// Highlight text helper function - for plain text (questions)
const highlightText = (text: string, searchTerm: string): React.ReactNode => {
  if (!searchTerm.trim()) {
    return text;
  }

  const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, index) => {
    if (part.toLowerCase() === searchTerm.toLowerCase()) {
      return (
        <Box
          key={index}
          component="mark"
          sx={{
            backgroundColor: '#ffeb3b',
            color: 'inherit',
            padding: '0 2px',
            borderRadius: '2px',
            fontWeight: 'inherit'
          }}
        >
          {part}
        </Box>
      );
    }
    return part;
  });
};

// Highlight text in HTML content (answers) - safely highlights text without breaking HTML tags
const highlightHtmlContent = (html: string, searchTerm: string): string => {
  if (!searchTerm.trim()) {
    return html;
  }

  const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');

  // Split by HTML tags to avoid highlighting inside tags
  const parts = html.split(/(<[^>]*>)/g);

  const highlightedParts = parts.map(part => {
    // If it's an HTML tag, don't modify it
    if (part.startsWith('<') && part.endsWith('>')) {
      return part;
    }
    // Otherwise, highlight matching text
    return part.replace(
      regex,
      '<mark style="background-color: #ffeb3b; color: inherit; padding: 0 2px; border-radius: 2px;">$1</mark>'
    );
  });

  return highlightedParts.join('');
};

export const FAQPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedPanel, setExpandedPanel] = useState<string | false>(false);

  const { categories, faqs } = useMemo(() => getFaqData(t as any), [t]);

  const stripHtmlTags = (html: string): string => {
    return html.replace(/<[^>]*>/g, '');
  };

  const filteredFAQs = useMemo(() => {
    return faqs.filter(faq => {
      const cleanAnswer = stripHtmlTags(faq.answer);

      const matchesSearch =
        searchTerm === '' ||
        faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cleanAnswer.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        selectedCategory === 'all' || faq.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [faqs, searchTerm, selectedCategory]);

  const groupedFAQs = useMemo(() => {
    const groups: { [key: string]: typeof faqs } = {};
    filteredFAQs.forEach(faq => {
      if (!groups[faq.category]) {
        groups[faq.category] = [];
      }
      groups[faq.category].push(faq);
    });
    return groups;
  }, [filteredFAQs]);

  const handleAccordionChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanel(isExpanded ? panel : false);
    };

  const getCategoryLabel = (categoryKey: string) => {
    const category = categories.find(c => c.key === categoryKey);
    return category ? category.label : categoryKey;
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Paper
          elevation={2}
          sx={{
            p: 4,
            mb: 4,
            textAlign: 'center',
            background: `linear-gradient(135deg, #a0130e10, #d4a57410)`
          }}
        >
          <HelpIcon sx={{ fontSize: 48, color: '#a0130e', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            {t('faq_title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            {t('faq_subtitle')}
          </Typography>
        </Paper>

        {/* Search and Filters */}
        <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t('faq_search_placeholder')}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
              size={isMobile ? 'small' : 'medium'}
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                {t('faq_categories')}
              </Typography>
              <Stack
                direction="row"
                sx={{
                  flexWrap: 'wrap',
                  gap: 1,
                  '& > *': { mb: 0.5 }
                }}
              >
                <Chip
                  label={t('faq_category_all')}
                  onClick={() => setSelectedCategory('all')}
                  variant={selectedCategory === 'all' ? 'filled' : 'outlined'}
                  color={selectedCategory === 'all' ? 'primary' : 'default'}
                  sx={{
                    fontWeight: selectedCategory === 'all' ? 600 : 400,
                    cursor: 'pointer',
                    '&.MuiChip-colorPrimary': {
                      backgroundColor: '#a0130e'
                    }
                  }}
                />
                {categories.map(category => (
                  <Chip
                    key={category.key}
                    label={category.label}
                    onClick={() => setSelectedCategory(category.key)}
                    variant={selectedCategory === category.key ? 'filled' : 'outlined'}
                    color={selectedCategory === category.key ? 'primary' : 'default'}
                    sx={{
                      fontWeight: selectedCategory === category.key ? 600 : 400,
                      cursor: 'pointer',
                      '&.MuiChip-colorPrimary': {
                        backgroundColor: '#a0130e'
                      }
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        </Paper>

        {/* Results count */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {t('faq_results_count', { count: filteredFAQs.length })}
          </Typography>
        </Box>

        {/* FAQ Accordions */}
        {filteredFAQs.length > 0 ? (
          <Stack spacing={4}>
            {Object.entries(groupedFAQs)
              .sort((a, b) => {
                const catA = categories.find(c => c.key === a[0]);
                const catB = categories.find(c => c.key === b[0]);
                return (catA?.order || 0) - (catB?.order || 0);
              })
              .map(([categoryKey, categoryFaqs]) => (
                <Box key={categoryKey}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: '#c00000',
                      borderBottom: '2px solid #c00000',
                      pb: 1
                    }}
                  >
                    {getCategoryLabel(categoryKey)}
                  </Typography>

                  <Stack spacing={1}>
                    {categoryFaqs.map((faq, index) => {
                      const panelId = `${categoryKey}-${index}`;
                      return (
                        <Fade in={true} timeout={300 + index * 30} key={panelId}>
                          <Accordion
                            elevation={1}
                            expanded={expandedPanel === panelId}
                            onChange={handleAccordionChange(panelId)}
                            sx={{
                              '&:before': { display: 'none' },
                              borderRadius: 1,
                              '&.Mui-expanded': {
                                boxShadow: theme.shadows[3]
                              }
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{
                                px: 3,
                                py: 1,
                                '&.Mui-expanded': {
                                  backgroundColor: 'action.hover'
                                }
                              }}
                            >
                              <Typography 
                                variant="subtitle1" 
                                component="div"
                                sx={{ 
                                  fontWeight: 500,
                                  color: '#000'
                                }}
                              >
                                {highlightText(faq.question, searchTerm)}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 3, py: 2, pt: 0 }}>
                              <Typography
                                variant="body2"
                                component="div"
                                sx={{
                                  lineHeight: 1.8,
                                  color: '#000',
                                  '& a': {
                                    color: '#00b0f0',
                                    textDecoration: 'underline',
                                    fontWeight: 600,
                                    '&:hover': {
                                      color: '#0090d0',
                                      textDecoration: 'underline'
                                    }
                                  },
                                  '& strong, & b': {
                                    fontWeight: 600,
                                    color: 'text.primary'
                                  },
                                  '& em, & i': {
                                    fontStyle: 'italic'
                                  },
                                  '& ul': {
                                    marginTop: 1,
                                    marginBottom: 1,
                                    paddingLeft: 3
                                  },
                                  '& li': {
                                    marginBottom: 0.5
                                  },
                                  '& mark': {
                                    backgroundColor: '#ffeb3b',
                                    color: 'inherit',
                                    padding: '0 2px',
                                    borderRadius: '2px'
                                  }
                                }}
                                dangerouslySetInnerHTML={{ 
                                  __html: highlightHtmlContent(faq.answer, searchTerm) 
                                }}
                              />
                            </AccordionDetails>
                          </Accordion>
                        </Fade>
                      );
                    })}
                  </Stack>
                </Box>
              ))}
          </Stack>
        ) : (
          <Paper elevation={1} sx={{ p: 6, textAlign: 'center' }}>
            <SearchIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('faq_no_results')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('faq_no_results_desc')}
            </Typography>
          </Paper>
        )}

        {/* Contact Support */}
        <Paper
          elevation={2}
          sx={{
            p: 4,
            mt: 6,
            textAlign: 'center',
            backgroundColor: '#a0130e',
            color: 'white'
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            {t('faq_still_need_help')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>
            {t('faq_contact_support')}
          </Typography>
          <Typography
            variant="h6"
            component="a"
            href="mailto:support@layerstock.com"
            sx={{
              color: 'inherit',
              textDecoration: 'underline',
              fontWeight: 600,
              '&:hover': {
                opacity: 0.8
              }
            }}
          >
            support@layerstock.com
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default FAQPage;